import { useCallback, useState } from 'react';

import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/utils/localstorage';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { OnboardingData } from './types';

const STORAGE_KEY = 'wac.loom-onboarding.data';
// Onboarding is short-lived, but we want to survive page reloads and brief
// drop-offs. 7 days is comfortably longer than any realistic flow duration.
const STORAGE_TTL_SECONDS = 60 * 60 * 24 * 7;

/**
 * Build the default onboarding data.
 *
 * All user-selectable fields (`useCase`, `role`, `recorder`) are intentionally
 * left undefined. Each step is responsible for seeding its own initial
 * selection (e.g. `persona-use-case-step` falls back to
 * `shouldAutoSelectLoomOnboardingWorkUseCase` when there is nothing cached),
 * so we don't want to overwrite a real user choice with a default written at
 * bootstrap time. This also means that when the user goes back to a previous
 * step, whatever they picked last time wins over any auto-select fallback.
 */
export const getDefaultOnboardingData = (aaid: string): OnboardingData => ({
	aaid,
	useCase: undefined,
	role: undefined,
	recorder: undefined,
});

const readFromStorage = (): OnboardingData | undefined => {
	const raw = getLocalStorage(STORAGE_KEY, true);
	if (!raw) {
		return undefined;
	}
	try {
		return JSON.parse(raw) as OnboardingData;
	} catch (error) {
		console.warn('Failed to read loom onboarding data from localStorage', error);
		return undefined;
	}
};

const writeToStorage = (data: OnboardingData): void => {
	try {
		setLocalStorage(STORAGE_KEY, JSON.stringify(data), STORAGE_TTL_SECONDS);
	} catch (error) {
		console.warn('Failed to save loom onboarding data to localStorage', error);
	}
};

const clearStorage = (): void => {
	try {
		removeLocalStorage(STORAGE_KEY);
	} catch (error) {
		console.warn('Failed to clear loom onboarding data from localStorage', error);
	}
};

/**
 * Seed a fresh onboarding record in localStorage. Call this once when the
 * flow starts so we overwrite any cached data left behind by a previous
 * user on the same browser.
 */
export const createOnboardingData = (aaid: string): OnboardingData => {
	const fresh = getDefaultOnboardingData(aaid);
	writeToStorage(fresh);
	return fresh;
};

export const useOnboardingData = () => {
	const { accountId = '' } = useLoggedInUser();

	// function to store the data as a state with default/empty fields.
	// Hydrates from localStorage on first render so a page reload mid-flow
	// keeps the user's progress.
	const [data, setData] = useState<OnboardingData>(
		() => readFromStorage() ?? getDefaultOnboardingData(accountId),
	);

	// function to update the data we have saved.
	//
	// We write to localStorage synchronously (outside the state updater)
	// rather than from inside `setData((prev) => ...)`. Callers commonly
	// invoke `updateOnboardingData(...)` and then immediately trigger a
	// step transition (e.g. persona step → download-options step) which
	// unmounts the component. If the write lived inside the state updater,
	// React can drop that pending update on unmount and localStorage would
	// never be written — the next step would then read stale/default data.
	// `readFromStorage()` is the source of truth here — the previous call
	// already wrote to localStorage synchronously, so we don't need `data`
	// in the closure. That keeps this callback referentially stable across
	// re-renders (only re-created if `accountId` changes).
	const updateOnboardingData = useCallback(
		(updates: Partial<OnboardingData>) => {
			const prev = readFromStorage() ?? getDefaultOnboardingData(accountId);
			const next = { ...prev, ...updates };
			writeToStorage(next);
			setData(next);
		},
		[accountId],
	);

	const clearOnboardingData = useCallback(() => {
		clearStorage();
		return data;
	}, [data]);

	return { data, updateOnboardingData, clearOnboardingData };
};
